import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Edit, Trash2, X, Save, Eye, EyeOff, Paintbrush } from 'lucide-react';
import VisualPageEditor from './VisualPageEditor';
import { getToken } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [visualEditingPageId, setVisualEditingPageId] = useState(null);

  useEffect(() => {
    loadPages();
    loadBlocks();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/pages`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setPages(data.pages || []);
    } catch {
    }
    setLoading(false);
  };

  const loadBlocks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cms/blocks`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setBlocks(data.blocks || []);
    } catch {
    }
  };

  const handleCreatePage = () => {
    setEditingPage({
      name: 'Nouvelle page',
      slug: 'nouvelle-page',
      title: '',
      description: '',
      blocks: [],
      meta: {}
    });
  };

  const handleSavePage = async () => {
    try {
      const url = editingPage.id
        ? `${API_URL}/api/cms/pages/${editingPage.id}`
        : `${API_URL}/api/cms/pages`;
      
      const method = editingPage.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(editingPage)
      });

      if (response.ok) {
        alert('✅ Page enregistrée !');
        setEditingPage(null);
        loadPages();
      } else {
        alert('❌ Erreur lors de la sauvegarde');
      }
    } catch {
      alert('❌ Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Supprimer cette page ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/cms/pages/${pageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        loadPages();
      }
    } catch {
    }
  };

  const togglePublish = async (page) => {
    try {
      const response = await fetch(`${API_URL}/api/cms/pages/${page.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_published: !page.is_published })
      });

      if (response.ok) {
        loadPages();
      }
    } catch {
    }
  };

  const addBlockToPage = (blockId) => {
    setEditingPage({
      ...editingPage,
      blocks: [...editingPage.blocks, blockId]
    });
  };

  const removeBlockFromPage = (index) => {
    const newBlocks = editingPage.blocks.filter((_, i) => i !== index);
    setEditingPage({ ...editingPage, blocks: newBlocks });
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...editingPage.blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newBlocks.length) return;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    setEditingPage({ ...editingPage, blocks: newBlocks });
  };

  const renderPageEditor = () => {
    if (!editingPage) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>
                {editingPage.id ? 'Éditer la page' : 'Nouvelle page'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditingPage(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nom de la page</label>
                <Input
                  value={editingPage.name}
                  onChange={(e) => setEditingPage({ ...editingPage, name: e.target.value })}
                  placeholder="Ex: Homepage"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Slug (URL)</label>
                <Input
                  value={editingPage.slug}
                  onChange={(e) => setEditingPage({ ...editingPage, slug: e.target.value })}
                  placeholder="Ex: home"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Titre (SEO)</label>
              <Input
                value={editingPage.title}
                onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })}
                placeholder="Titre de la page pour le SEO"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description (SEO)</label>
              <Textarea
                value={editingPage.description}
                onChange={(e) => setEditingPage({ ...editingPage, description: e.target.value })}
                placeholder="Description de la page"
                rows={3}
              />
            </div>

            {/* Blocks Composition */}
            <div>
              <label className="block text-sm font-medium mb-2">Composition de la page (Blocs)</label>
              <div className="border rounded-lg p-4 space-y-2 mb-4 bg-gray-50">
                {editingPage.blocks.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Aucun bloc ajouté</p>
                ) : (
                  editingPage.blocks.map((blockId, index) => {
                    const block = blocks.find(b => b.id === blockId);
                    return (
                      <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                        <span className="text-gray-500 text-sm">{index + 1}.</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{block?.name || 'Bloc inconnu'}</p>
                          <p className="text-xs text-gray-500">{block?.type}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveBlock(index, 'up')}
                            disabled={index === 0}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveBlock(index, 'down')}
                            disabled={index === editingPage.blocks.length - 1}
                          >
                            ↓
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeBlockFromPage(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Ajouter un bloc :</p>
                <div className="grid grid-cols-2 gap-2">
                  {blocks.map(block => (
                    <Button
                      key={block.id}
                      variant="outline"
                      size="sm"
                      onClick={() => addBlockToPage(block.id)}
                      className="justify-start"
                    >
                      <Plus className="h-3 w-3 mr-2" />
                      {block.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingPage(null)}>
                Annuler
              </Button>
              <Button onClick={handleSavePage}>
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Gestionnaire de Pages</CardTitle>
              <p className="text-sm text-gray-500 mt-1">{pages.length} page(s)</p>
            </div>
            <Button onClick={handleCreatePage}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle page
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : pages.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500 mb-2">Aucune page trouvée</p>
              <Button onClick={handleCreatePage} variant="outline" size="sm">
                Créer votre première page
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {pages.map(page => (
                <Card key={page.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{page.name}</h3>
                          {page.is_published ? (
                            <span className="text-xs bg-green-700 text-white px-2 py-1 rounded">
                              ✅ Publié
                            </span>
                          ) : (
                            <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded">
                              📝 Brouillon
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">/{page.slug}</p>
                        <p className="text-xs text-gray-500">{page.blocks.length} bloc(s)</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePublish(page)}
                          title={page.is_published ? "Dépublier" : "Publier"}
                        >
                          {page.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setVisualEditingPageId(page.id)}
                          title="Éditeur visuel WYSIWYG"
                        >
                          <Paintbrush className="h-4 w-4 text-purple-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPage(page)}
                          title="Paramètres de la page"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePage(page.id)}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {renderPageEditor()}

      {/* Visual Page Editor */}
      {visualEditingPageId && (
        <div className="fixed inset-0 z-50 bg-white">
          <VisualPageEditor 
            pageId={visualEditingPageId} 
            onClose={() => {
              setVisualEditingPageId(null);
              loadPages();
            }}
          />
        </div>
      )}
    </>
  );
};

export default PagesManager;